import type { ButtonProps as AriaButtonProps } from 'react-aria-components';

enum HoverActionButtonSize {
  Xs = 'xs',
  Sm = 'sm',
  Md = 'md',
  Lg = 'lg',
  Xl = 'xl',
}

enum HoverActionButtonHierarchy {
  Primary = 'primary',
  Secondary = 'secondary',
  Destructive = 'destructive',
}

interface HoverActionButtonProps extends Omit<AriaButtonProps, 'children' | 'className'> {
  /**
   * Accessible name for the icon-only button.
   * @required
   * @type string
   */
  'aria-label': string;
  /**
   * The icon rendered centered in the button. Constant 20×20 across all sizes
   * per spec — pass `size={20}` to the icon.
   * @required
   * @type React.ReactNode
   */
  icon: React.ReactNode;
  /**
   * Button dimensions: xs 28px, sm 36px, md 40px, lg 44px, xl 48px.
   * @optional
   * @type HoverActionButtonSize
   * @default HoverActionButtonSize.Sm
   */
  size?: HoverActionButtonSize;
  /**
   * Visual hierarchy. Primary: white on light surfaces. Secondary: dark
   * overlay, whitening on hover. Destructive: dark overlay, turning error red
   * on hover.
   * @optional
   * @type HoverActionButtonHierarchy
   * @default HoverActionButtonHierarchy.Primary
   */
  hierarchy?: HoverActionButtonHierarchy;
  /**
   * Replaces the icon with a spinner and blocks interaction.
   * @optional
   * @type boolean
   * @default false
   */
  isLoading?: boolean;
  /**
   * Pass-through class for the button element.
   * @optional
   * @type string
   */
  className?: string;
}

export { HoverActionButtonHierarchy, HoverActionButtonSize };
export type { HoverActionButtonProps };
